import React, { useState, useEffect } from 'react';
import { Save, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { roleService } from '../../../services/roleService';

export default function PermissionMatrix({ roleId }) {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const features = roleService?.getSystemFeatures();
  const accessLevels = roleService?.getAccessLevels();

  useEffect(() => {
    if (roleId) {
      loadPermissions();
    }
  }, [roleId]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError('');
      setValidationWarnings([]);
      const data = await roleService?.getRolePermissions(roleId);
      
      const permMap = {};
      data?.forEach(p => {
        permMap[p?.feature] = p?.accessLevel;
      });
      
      setPermissions(permMap);
      setHasChanges(false);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = async (feature, level) => {
    const updatedPermissions = { ...permissions, [feature]: level };
    setPermissions(updatedPermissions);
    setHasChanges(true);
    setSuccessMessage('');

    // Real-time validation
    try {
      const permissionsArray = Object.entries(updatedPermissions)?.map(([feat, lvl]) => ({
        feature: feat,
        accessLevel: lvl
      }));

      const validation = await roleService?.validateRolePermissions(roleId, permissionsArray);
      
      if (!validation?.isValid && validation?.conflicts?.length > 0) {
        setValidationWarnings(validation?.conflicts);
      } else {
        setValidationWarnings([]);
      }
    } catch (err) {
      console.error('Validation error:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const permissionsArray = Object.entries(permissions)?.map(([feature, level]) => ({
        feature,
        accessLevel: level
      }));

      // Use validation-aware update method
      await roleService?.updateRolePermissionsWithValidation(roleId, permissionsArray);
      
      setSuccessMessage('Permissions updated successfully');
      setHasChanges(false);
      setValidationWarnings([]);
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const getAccessLevelColor = (level) => {
    switch (level) {
      case 'none': return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'read': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'write': return 'bg-green-100 text-green-800 border-green-300';
      case 'delete': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'admin': return 'bg-purple-100 text-purple-800 border-purple-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Validation Warnings */}
      {validationWarnings?.length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-yellow-900 mb-2">
                Permission Conflicts Detected
              </h3>
              <ul className="space-y-2 text-sm text-yellow-800">
                {validationWarnings?.map((conflict, index) => (
                  <li key={index} className="flex items-start">
                    <span className="inline-block w-2 h-2 bg-yellow-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                    <div>
                      <span className="font-medium">{conflict?.feature1}</span> ({conflict?.accessLevel1})
                      {' ↔ '}
                      <span className="font-medium">{conflict?.feature2}</span> ({conflict?.accessLevel2})
                      <p className="text-yellow-700 mt-1">{conflict?.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-sm font-medium text-yellow-900">
                Please resolve these conflicts before saving.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
            <p className="text-sm font-medium text-green-900">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900 mb-1">Error Saving Permissions</h3>
              <p className="text-sm text-red-800 whitespace-pre-wrap">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-blue-900">
              Permission changes are validated in real-time to prevent contradictory access levels across system features.
            </p>
          </div>
        </div>
      </div>

      {/* Permission Matrix */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Feature
              </th>
              {accessLevels?.map(level => (
                <th
                  key={level?.id}
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <div className="flex flex-col items-center">
                    <span>{level?.name}</span>
                    <span className="text-xs text-gray-400 font-normal normal-case mt-1">
                      {level?.description}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {features?.map(feature => (
              <tr key={feature?.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-gray-900">
                      {feature?.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {feature?.description}
                    </div>
                  </div>
                </td>
                {accessLevels?.map(level => {
                  const isSelected = permissions?.[feature?.id] === level?.id;
                  return (
                    <td key={level?.id} className="px-6 py-4 text-center">
                      <button
                        onClick={() => handlePermissionChange(feature?.id, level?.id)}
                        className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
                          isSelected
                            ? getAccessLevelColor(level?.id)
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {isSelected && <CheckCircle className="w-3 h-3 mr-1" />}
                        {level?.name}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save Button */}
      <div className="mt-6 flex items-center justify-end space-x-3">
        {hasChanges && (
          <span className="text-sm text-gray-500">You have unsaved changes</span>
        )}
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges || validationWarnings?.length > 0}
          className={`inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            saving || !hasChanges || validationWarnings?.length > 0
              ? 'bg-gray-400 cursor-not-allowed' :'bg-indigo-600 hover:bg-indigo-700'
          }`}
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}